import { Link } from 'react-router-dom';
import Reveal from '../ui/Reveal';
import { ArrowRight } from '../ui/Icons';

type Room = { id: string; name: string; copy: string; image?: string; swatches: string[] };

/**
 * Rooms with a real photograph use it. Rooms we don't yet have photography for
 * (kitchen, dining, bathroom) are shown as a painted composition of their own
 * palette rather than a mislabelled photo of another room.
 */
const ROOMS: Room[] = [
  { id: 'living-room', name: 'Living room', copy: 'Warm neutrals and a feature wall', image: '/images/web/living.jpg', swatches: ['#DCE6EF', '#EFE7D6', '#33427E'] },
  { id: 'bedroom', name: 'Bedroom', copy: 'Restful greens and soft greys', image: '/images/web/bedroom.jpg', swatches: ['#C9D8C5', '#EDEAF4', '#6E7480'] },
  { id: 'kitchen', name: 'Kitchen', copy: 'Bright, washable and easy to clean', swatches: ['#F4EBD6', '#F2B632', '#A8BFA6'] },
  { id: 'dining-room', name: 'Dining', copy: 'Rich tones for long dinners', swatches: ['#EEE7D6', '#C4653F', '#4F6355'] },
  { id: 'study', name: 'Study', copy: 'Calm blues that help you focus', image: '/images/web/study.jpg', swatches: ['#BFD0E6', '#F3F2E6', '#3E5C77'] },
  { id: 'bathroom', name: 'Bathroom', copy: 'Moisture-resistant, spa-like tones', swatches: ['#DDEBEA', '#F3F3EA', '#507BB2'] },
  { id: 'exterior', name: 'Exterior', copy: 'Weather-tough façade schemes', image: '/images/web/house-lattice.jpg', swatches: ['#EDE4D3', '#9E5540', '#3E5C77'] },
];

/** A wall-and-accent composition painted in the room's palette. */
const PaletteArt = ({ swatches }: { swatches: string[] }) => {
  const [wall, accent, trim] = swatches;
  return (
    <div className="absolute inset-0 transition-transform duration-[1200ms] ease-premium group-hover:scale-110" aria-hidden="true">
      <div className="absolute inset-0" style={{ background: wall }} />
      <div className="absolute inset-y-0 right-0 w-[38%]" style={{ background: accent }} />
      <div className="absolute inset-x-0 bottom-0 h-[16%]" style={{ background: trim }} />
      <div className="absolute inset-0 bg-gradient-to-br from-white/25 via-transparent to-black/10" />
      <div className="absolute top-[18%] left-[12%] w-[28%] aspect-square rounded-lg border-[6px] border-white/80 shadow-lg" style={{ background: accent }} />
    </div>
  );
};

/**
 * Edge-to-edge room grid: one 2x2 feature tile and six supporting tiles
 * (exactly fills five columns by two rows on desktop), with the copy in a
 * white box on each tile.
 */
const ExploreByRoom = () => (
  <section className="bg-white pt-16 md:pt-20">
    <div className="container mb-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
      <div className="max-w-2xl">
        <span className="vp-eyebrow">Explore by room</span>
        <h2 className="vp-heading mt-4 text-3xl sm:text-4xl lg:text-[2.75rem]">Colour ideas for every room in the house</h2>
        <p className="mt-4 text-brand-muted leading-relaxed">
          Each room has its own light, its own use and its own mood. Start with a palette our colour team has already tested in that space.
        </p>
      </div>
      <Link to="/colours/rooms" className="vp-btn vp-btn-outline self-start flex-shrink-0">
        All rooms
        <ArrowRight />
      </Link>
    </div>

    <div className="grid grid-cols-2 lg:grid-cols-5 lg:grid-rows-2 gap-1 lg:h-[720px]">
      {ROOMS.map((r, i) => {
        const feature = i === 0;
        return (
          <Link
            key={r.id}
            to={`/colours/${r.id}`}
            className={`group relative overflow-hidden ${feature ? 'col-span-2 lg:row-span-2 h-[420px] lg:h-auto' : 'h-[260px] sm:h-[320px] lg:h-auto'}`}
          >
            {r.image ? (
              <img src={r.image} alt={`${r.name} painted in a Vertex colour scheme`} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-premium group-hover:scale-110" />
            ) : (
              <PaletteArt swatches={r.swatches} />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/55 via-transparent to-transparent" />
            <Reveal delay={(Math.min(i, 3) as 0 | 1 | 2 | 3)} className={`absolute left-3 right-3 bottom-3 ${feature ? 'sm:left-8 sm:bottom-8 sm:right-auto sm:max-w-sm' : 'sm:left-4 sm:right-4 sm:bottom-4'}`}>
              <div className="bg-white/95 backdrop-blur rounded-2xl shadow-lift p-4 sm:p-5">
                <div className="flex items-center justify-between gap-3">
                  <h3 className={`font-bold text-brand-ink ${feature ? 'text-2xl' : 'text-base sm:text-lg'}`}>{r.name}</h3>
                  <span className="flex -space-x-1.5 flex-shrink-0">
                    {r.swatches.map((c) => (
                      <span key={c} className="w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 border-white shadow-sm" style={{ background: c }} />
                    ))}
                  </span>
                </div>
                <p className={`mt-1 text-brand-muted ${feature ? 'text-sm' : 'hidden sm:block text-xs'}`}>{r.copy}</p>
                {feature && (
                  <span className="mt-3 vp-link text-sm">
                    See the palette <ArrowRight size={14} />
                  </span>
                )}
              </div>
            </Reveal>
          </Link>
        );
      })}
    </div>
  </section>
);

export default ExploreByRoom;
